import React, { useState, useRef, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  RBTextFieldForForm,
  RBSelectForForm,
  RBTextFieldForFormFieldType,
  RBMultipleSelectForForm,
} from "../../../components/RBTextField";
import { Colors, Spacing } from "../../../theme/Theme";
import { RBPlaceSearchForForm } from "../../../components/RBPlaceSearch";
import { CanadaProvinces, PharmacyRxCounts, InvoiceRecipientContactMethod,EmptyString } from "../../../util/Common";
import { SkillResponseDto } from "../../../dtos/Skill.dto";
import { PharmacyService } from "../../../api/PharmacyService";
import { formattedLocationTypeEnum, SkillType } from "../../../util/Enumeration";
import { ObjectLiteral } from "../../../interfacesProps/ObjectLiteralProps";
import { MenuItem, Checkbox, FormControlLabel, Typography,ListItemText } from "@material-ui/core";
import { LabelText } from "../../../util/TextConstant";
import { Schema } from "../YupSchema";
import { AuthService } from "../../../api/AuthService";
import { IndustryModeEnum } from "../../../api/constants";
import { ColumnStyle,skillTextStyle } from "../../../theme/ComponentTheme";
import { RBEmptyBlock } from "../../../components/RBEmptyBlock";
import { Regexes } from "../../../util/Default";
import _ from "lodash";

export interface DentalPracticeInformationValueTypes {
  pharmacyName: string;
  street: string;
  city: string;
  postalCode: string;
  province: string;
  pharmacyPhoneNumber: string;
  apartmentSuiteNumber?: string;
  companyType: string;
  InvoiceRecipientContact: string;
  billingEmail?: string;
  isBillingEnabled: boolean;
  [key: string]: any;
}

interface DentalPracticeInformationProps {
  innerRef?: any;
  onFormChange: any;
  initialValues: DentalPracticeInformationValueTypes;
  ownerType?: formattedLocationTypeEnum;
  account?: any;
  industryMode?: any;
  onSkillsChange?: any;
}

export const DentalPracticeInformation: React.FC<DentalPracticeInformationProps> = ({
  ...props
}) => {
  const [DentalOwnerPosition, setDentalOwnerPosition] = useState<any>([]);
  const [isBillingEnabled, setIsBillingEnabled] = useState(true);
  const [invoiceRecipientArr, setInvoiceRecipientArr] = useState<any>([InvoiceRecipientContactMethod[0]]);
  const [skillsByType, setSkillsByType] = useState<Record<string, SkillResponseDto[]>>({});
 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const skillSet = props.account?.networks?.[0]?.skillSet;
        const locationSkillSets = skillSet?.locationSkillSets || [];
        const skillsResponse = await PharmacyService.fetchSkills(locationSkillSets, props.industryMode);
        const rawSkills = skillsResponse.data;

        // Group skills by their `type`
        const groupedSkills = rawSkills.reduce((acc:any, skill:any) => {
          const type = skill.type || 'unknown'; // Fallback if type is missing
          if (!acc[type]) {
            acc[type] = [];
          }
          acc[type].push(skill);
          return acc;
        }, {} as Record<string, any[]>);

        // Now set the grouped result
        setSkillsByType(groupedSkills);

        const dentalCompanyTypes = await AuthService.getLocationTypesBasedonIndustry(props.industryMode);
        const DentalOfficeCompanyType = dentalCompanyTypes.map((item:any) => item.locationType);
        setDentalOwnerPosition(DentalOfficeCompanyType)
      } catch (err) {}
    };
    fetchData();
  }, []);

  useEffect(() => {
    props.onSkillsChange?.(skillsByType); // whenever skills state updates
  }, [skillsByType]);

  useEffect(() => {
    if (props.initialValues.InvoiceRecipientContact === InvoiceRecipientContactMethod[0].id) {
      setIsBillingEnabled(true);
      setInvoiceRecipientArr([InvoiceRecipientContactMethod[0]]);
    } else {
      setIsBillingEnabled(false);
      setInvoiceRecipientArr(InvoiceRecipientContactMethod.filter(contact => contact.id !== InvoiceRecipientContactMethod[0].id));
    }
  }, [props.initialValues.InvoiceRecipientContact]);

  //const validationSchema = Schema.DentalPracticeInformation(props.account?.email);

  const handleCheckboxChange = (checked:boolean, setFieldValue:any) => {
    if (checked) {
          setInvoiceRecipientArr([InvoiceRecipientContactMethod[0]]);
          setFieldValue('InvoiceRecipientContact', InvoiceRecipientContactMethod[0].id);
          setFieldValue('billingEmail', "");
        } else {
          setInvoiceRecipientArr(InvoiceRecipientContactMethod.filter(contact => contact.id !== InvoiceRecipientContactMethod[0].id));
          setFieldValue('InvoiceRecipientContact', InvoiceRecipientContactMethod[2].id);
          setFieldValue('billingEmail', "", true);
        }
  }
  const ownerEmail = props.account?.email;
  const locationMultiSelects = props.account.networks[0].skillSet.locationMultiSelects || [];
  const DentalPracticeInformation = (ownerEmail:any,skillTypes: string[]) => {
    const shape: any = {
        pharmacyName: Yup.string().required("Required"),
      street: Yup.string().required("Required"),
      city: Yup.string().required("Required"),
      postalCode: Yup.string()
        .required("Required")
        .matches(Regexes.CanadaPostalCodeValidation, "Invalid postal code"),
      province: Yup.string().required("Required"),
      pharmacyPhoneNumber: Yup.string()
        .required("Required")
        .matches(Regexes.PhoneValidation, "Invalid phone number"),
      companyType: Yup.string().required("Required"),
      InvoiceRecipientContact: Yup.string().required("Required"),
    }
     // Add dynamic skill fields
        skillTypes.forEach(type => {
          const fieldName = `skills${_.startCase(type).replace(/\s/g, '')}`;
          shape[fieldName] = Yup.string().required("Required");
        });

        shape.billingEmail = isBillingEnabled === false ?
        Yup.string()
        .required("Required")
        .matches(Regexes.emailPattern, "must be a valid email address")
        .test("is-not-owner-email", "Billing email is same as owner email", function (value) {
          console.log("isBillingEnabled in validation:", isBillingEnabled);
          return value?.toLocaleLowerCase() !== ownerEmail?.toLocaleLowerCase();
        })
      : Yup.string().notRequired()

      return Yup.object().shape(shape);
  }

 const skillFieldInitialValues = Object.keys(skillsByType).reduce<Record<string, string>>((acc, type) => {
  const fieldName = `skills${_.startCase(type).replace(/\s/g, '')}`;
  acc[fieldName] = props.initialValues[fieldName] || '';
  return acc;
}, {});

  // Merge with rest of initial values
  const formInitialValues = {
    ...props.initialValues,
    ...skillFieldInitialValues
  };

  return (
    <Formik
      initialValues={formInitialValues}
      innerRef={props.innerRef}
      validationSchema={DentalPracticeInformation(ownerEmail, Object.keys(skillsByType))}
      validateOnBlur={true}
      validateOnMount={true}
      validateOnChange={true}
      isInitialValid={DentalPracticeInformation(ownerEmail, Object.keys(skillsByType)).isValidSync(
        formInitialValues
      )}
      onSubmit={() => {
        console.log("sdasdas");
      }}
    >
      {({
        values,
        errors,
        isSubmitting,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        validateField
      }) => (
        <Form
          onSubmit={handleSubmit}
          // basically call onFormChange to check if the form is valid
          onKeyUp={props.onFormChange}
          onMouseDown={props.onFormChange}
        >
          <RBTextFieldForForm
            label={props.industryMode === IndustryModeEnum.Dentistry ?  "Practice Name" : "Company Name"}
            name="pharmacyName"
            value={values.pharmacyName}
            onChange={handleChange}
            onBlur={handleBlur}
            size={"small"}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          <RBSelectForForm
            label={props.industryMode === IndustryModeEnum.Dentistry ?  "Practice Type" : "Company Type"}
            name="companyType"
            value={values.companyType}
            mappingValues={DentalOwnerPosition}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          <div
            style={{
              marginBottom: Spacing.large,
            }}
          >
            <RBPlaceSearchForForm
              label="Street Address"
              name="street"
              value={values.street}
              setFieldValue={(addressObject: any) => {
                if ('street' in addressObject) setFieldValue("street", addressObject.street, true);
                if ('city' in addressObject) setFieldValue("city", addressObject.city, true);
                if ('postalCode' in addressObject) setFieldValue("postalCode", addressObject.postalCode, true);
                if ('province' in addressObject) setFieldValue("province", addressObject.province, true);
              }}
              onBlur={handleBlur}
            />
          </div>

          <RBTextFieldForForm
            label="Apartment/Suite #"
            placeholder="Optional"
            name="apartmentSuiteNumber"
            value={values.apartmentSuiteNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            size={"small"}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          <RBTextFieldForForm
            label="City"
            name="city"
            value={values.city}
            onChange={handleChange}
            onBlur={handleBlur}
            size={"small"}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          <RBTextFieldForForm
            label="Postal Code"
            name="postalCode"
            FieldType={RBTextFieldForFormFieldType.postalCode}
            value={values.postalCode}
            onChange={handleChange}
            onBlur={handleBlur}
            size={"small"}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          <RBSelectForForm
            label="Province"
            name="province"
            value={values.province}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={"Province"}
            mappingValues={CanadaProvinces.map(
              (province: any) => province.abbr
            )}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          <RBTextFieldForForm
            label="Phone Number"
            name="pharmacyPhoneNumber"
            FieldType={RBTextFieldForFormFieldType.phone}
            value={values.pharmacyPhoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            size={"small"}
            style={{
              marginBottom: Spacing.large,
            }}
          />

          {Object.keys(skillsByType)?.map((type) => {
            const skillList = skillsByType[type];
            const fieldName = `skills${_.startCase(type).replace(/\s/g, '')}`;
            const isMultiSelect = locationMultiSelects.includes(type);
            return (
              !isMultiSelect ? (
                <RBSelectForForm
                  key={type}
                  label={_.startCase(type)} // "Software", "Specialization", etc.
                  name={fieldName}
                  value={values[fieldName]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={`Select ${_.startCase(type)}`}
                  style={{
                    marginBottom: Spacing.large,
                  }}
                >
                  {skillList?.map((skill) => (
                    <MenuItem key={skill.id} value={skill.id}>
                      {skill.name}
                    </MenuItem>
                  ))}
                </RBSelectForForm>
              ) :
                (<RBMultipleSelectForForm
                  key={type}
                  label={type.includes('specialization') ? 'Skills' : _.startCase(type)} // "Software", "Specialization", etc.
                  name={fieldName}
                  value={values[fieldName] || []}
                  onChange={handleChange}
                  renderValue={(selected) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                      }}
                    >
                      {(selected as string[]).map((skillID) => {
                        const skill = skillList.find((s) => s.id === skillID);
                        return (
                          <div key={skillID} style={skillTextStyle}>
                            {skill?.name}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  style={{ marginBottom: Spacing.larger }}
                >
                  {skillList.map((skill) => (
                    <MenuItem key={skill.id} value={skill.id}>
                      <Checkbox
                        checked={values[fieldName]?.indexOf(skill.id) > -1}
                        style={{
                          color:
                            values[fieldName]?.indexOf(skill.id) > -1
                              ? localStorage.getItem("secondaryColor") ?? Colors.themeLightBlue
                              : Colors.darkGrey,
                        }}
                      />
                      <ListItemText primary={skill.name} />
                    </MenuItem>
                  ))}
                </RBMultipleSelectForForm>)
            );
          })}
          
          <div style={{ ...ColumnStyle, alignItems: "center" }}>
            <Typography style={{ display: "flex", flex: 1 }}>
              Billing email is same as owner email?
            </Typography>
            <RBEmptyBlock />
            <Checkbox
              checked={isBillingEnabled}
              style={{
                color: isBillingEnabled
                  ? localStorage.getItem("secondaryColor") ?? Colors.themeLightBlue
                  : Colors.darkGrey,
              }}
              name="isBillingEnabled"
              onBlur={handleBlur}
              onChange={async (_, checked) => {
                setIsBillingEnabled(!isBillingEnabled)
                setFieldValue("isBillingEnabled", checked, false);
                await handleCheckboxChange (checked,setFieldValue)
              }}
            />

          </div>
        {(!isBillingEnabled) &&
          <RBTextFieldForForm
            label="Billing Email"
            placeholder="Billing Email"
            name="billingEmail"
            value={values.billingEmail}
            onChange={handleChange}
            onBlur={handleBlur}
            size={"small"}
            style={{
              marginBottom: Spacing.large,
            }}
          />
          }
        

          <RBSelectForForm
            label="Invoice Recipient"
            name="InvoiceRecipientContact"
            value={values.InvoiceRecipientContact}
            onChange={async (e:any) => {
              const { value } = e.target;
              handleChange(e)
              await setFieldValue('InvoiceRecipientContact', value);

              // Force validate the InvoiceRecipientContact field immediately
              validateField('InvoiceRecipientContact');
              
            }
            }
            onBlur={handleBlur}
            placeholder={"Please select ..."}
            style={{
              marginBottom: Spacing.large,
            }}
          >
            {invoiceRecipientArr.map((recipient:any) => (
              <MenuItem key={recipient.id} value={recipient.id}>
                <div>{recipient.value}</div>
              </MenuItem>
            ))}
          </RBSelectForForm>
        </Form>
      )}
    </Formik>
  );
};
